import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import FETCHER from "./_fetcher";
import {
  ICreateOrder,
  ICreateOrderResponse,
  IOrder,
  IOrderItems,
  IPaymentNotiModalField,
} from "types/order";
import { IResponse } from "types/api";
import { E_ORDER_STATUS } from "constants/common";
import {
  trackPurchaseComplete,
  trackViewOrderDetails,
} from "../utils/analytics";

export const checkoutCart = async (orderDetail: ICreateOrder) => {
  const result = await FETCHER.post<ICreateOrderResponse>(
    `/order`,
    orderDetail
  );
  return result;
};

export const useCheckoutCart = () => {
  const { data, mutateAsync, isPending } = useMutation({
    mutationFn: checkoutCart,
    onSuccess: (response, orderDetail) => {
      // Track purchase completion
      if (response?.data) {
        const orderData = response.data;
        // Calculate discount amount (if not available directly)
        const discountAmount = 0; // Default to 0 if no discount info available

        trackPurchaseComplete(
          orderData.id,
          orderData.orderItems || [],
          orderData.totalAmount,
          discountAmount,
          orderDetail.paymentMethod,
          orderDetail.deliveryMethod, // Using deliveryMethod as shippingMethod
          orderDetail.pointsAsCashToUse
        );
      }
    },
  });
  return {
    data: data?.data,
    checkoutCartAsync: mutateAsync,
    isPending,
  };
};

export const getOrderDetail = async (orderId: string) => {
  try {
    const result = await FETCHER.get<IOrder>(`/order/${orderId}`);
    return result.data;
  } catch (e) {
    console.log(e);
  }
};
export const useGetOrderDetail = (orderId: string) => {
  const { data, isLoading } = useQuery({
    queryKey: ["getOrderDetail", orderId],
    queryFn: () => getOrderDetail(orderId),
    placeholderData: keepPreviousData,
    enabled: !!orderId,
  });

  // Track order detail view when data is loaded
  if (data) {
    trackViewOrderDetails(orderId);
  }

  return {
    orderDetail: data,
    isLoading,
  };
};

export const getMyOrderList = async (
  status?: E_ORDER_STATUS,
  page?: number,
  limit?: number
) => {
  try {
    let qs = `order/my-orders?page=${page || 1}&limit=${limit || 20}`;
    if (status && status !== E_ORDER_STATUS.ALL) {
      qs += `&status=${status}`;
    }
    const result = await FETCHER.get<IResponse<IOrder[]>>(qs);
    return result.data;
  } catch (e) {
    console.log(e);
  }
};
export const useGetMyOrderList = (
  status?: E_ORDER_STATUS,
  page?: number,
  limit?: number
) => {
  const { data, isLoading } = useQuery({
    queryKey: ["getMyOrderList", status, page, limit],
    queryFn: () => getMyOrderList(status, page, limit),
    placeholderData: keepPreviousData,
  });
  return {
    orderList: data,
    isLoading,
  };
};

export const updateOrderPayment = async (
  orderDetail: IPaymentNotiModalField
) => {
  const result = await FETCHER.post(
    `/order/my-orders/${orderDetail.orderNumber}/paid`,
    orderDetail
  );
  return result;
};

export const useUpdateOrderPayment = () => {
  const queryClient = useQueryClient();

  const { data, mutateAsync, isPending } = useMutation({
    mutationFn: updateOrderPayment,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["getOrderDetail", `${data.data.id}`],
      });
    },
  });
  return {
    data: data?.data,
    updateOrderPaymentAsync: mutateAsync,
    isPendingUpdateOrderPayment: isPending,
  };
};

// Repeat order API call
export const repeatOrder = async (orderId: number) => {
  try {
    const result = await FETCHER.post<IOrderItems[]>(`/order/repeatOrder`, {
      orderId,
    });
    return result.data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const useRepeatOrder = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: repeatOrder,
    onSuccess: () => {
      // Invalidate cart queries to refresh cart after adding items
      queryClient.invalidateQueries({
        queryKey: ["getCart"],
      });
    },
  });

  return {
    repeatOrderAsync: mutateAsync,
    isPendingRepeatOrder: isPending,
  };
};
